import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
// eslint-disable-next-line no-restricted-imports
import { apiCommon } from 'src/api/ApiCommon';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getTopNavigationLinks';
import { FilterState } from 'src/domains/layouts/shared/filter/Filter.state';
import { filtersGlobal } from 'src/components/GlobalPageV2/GlobalPageV2.types';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

export type LandingPageContentResponseType =
    | HeaderNavigationLinkLoadinType
    | PromotionLandingPageReadyType
    | HeaderNavigationLinkErrorType;

interface HeaderNavigationLinkLoadinType {
    type: 'loading';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HeaderNavigationLinkType = any | null;
interface PromotionLandingPageReadyType {
    type: 'ready';
    value: Array<HeaderNavigationLinkType>;
}

interface HeaderNavigationLinkErrorType {
    type: 'error';
    value: string;
}

export interface PagesGetType {
    title: string;
    content: string;
    slug: string;
    page_description: string;
    keywords: string;
    seo_title: string;
    page_language: string;
    id?: number;
}

export class PageItem {
    public constructor(
        public title: string,
        public content: string,
        public slug: string,
        public page_description: string,
        public keywords: string,
        public seo_title: string,
        public page_language: string,
        public universe: string | null
    ) {}
}

export interface PageType {
    slug: string;
    title: string;
    content: string;
    page_description: string;
    keywords: string;
    seo_title: string;
}

type CurrentPageType = 'edit' | 'add';

export class PagesStateV2 {
    @observable public readonly pagesResource: Resource<Response200Type | null>;
    @observable public readonly universe: string | null;
    @observable public page: string = 'add';
    @observable public editedPage: PagesGetType | null = null;
    // @observable imgErrorMessage: string | null = null;
    @observable public isLoading: boolean = false;
    // @observable errorMessage: string = '';
    @observable public filterLanguage: string = 'en-gb';
    @observable public language: string = 'en-gb';
    @observable public filters;

    public readonly slug: FormInputState<string, string>;

    public readonly title: FormInputState<string, string>;
    public readonly page_description: FormInputState<string, string>;
    public readonly keywords: FormInputState<string, string>;
    public readonly seo_title: FormInputState<string, string>;
    public readonly content: FormInputState<string, string>;

    private readonly form: FormModel<PageType>;

    public constructor(universe: string | null) {
        this.universe = universe;
        this.pagesResource = new Resource(async (): Promise<Response200Type | null> => {
            this.isLoading = true;
            const response = await apiCommon.getPagesList.run({ language: this.filterLanguage });
            this.isLoading = false;
            //@ts-expect-error
            return response;
        });

        this.slug = FormInputState.new('').map(validateNotEmpty('This field cant be empty'));
        this.title = FormInputState.new('').map(validateNotEmpty('This field cant be empty'));
        this.page_description = FormInputState.new('');
        this.keywords = FormInputState.new('');
        this.seo_title = FormInputState.new('');
        this.content = FormInputState.new('');

        this.form = FormModel.group({
            slug: this.slug,
            title: this.title,
            page_description: this.page_description,
            keywords: this.keywords,
            seo_title: this.seo_title,
            content: this.content,
        });

        this.filters = new FilterState(filtersGlobal, 'show-all');
        makeAutoObservable(this);
    }

    @computed public get allPages(): LandingPageContentResponseType {
        const pages = this.pagesResource.get();
        if (pages.type === 'ready' && pages.value !== null) {
            return {
                type: 'ready',
                value: pages.value,
            };
        }
        if (pages.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching landing page info',
        };
    }

    @computed public get isEditMode(): boolean {
        return this.page === 'edit';
    }

    @action public handleCancel = (): void => {
        this.form.reset();
        this.editedPage = null;
        this.page = 'add';
    };

    @action public setEditedPage = (editedPage: PagesGetType): void => {
        this.setPage('edit');

        this.editedPage = editedPage;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (editedPage !== null && editedPage !== undefined) {
            this.title.setValue(editedPage.title);
            this.content.setValue(editedPage.content);
            this.slug.setValue(editedPage.slug);
            this.page_description.setValue(editedPage.page_description);
            this.keywords.setValue(editedPage.keywords);
            this.seo_title.setValue(editedPage.seo_title);
            this.language = editedPage.page_language;
        }
    };

    @action public setFilterLanguage = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const lang = event.target.value;
        this.filterLanguage = lang;
        await this.pagesResource.refresh();
    };

    @action public setLanguage = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const lang = event.target.value;
        this.language = lang;
    };

    @computed public get isFormValid(): boolean {
        return this.form.result.value.type === 'ok';
    }

    @computed public get allPagesSorted(): Array<PagesGetType> | null {
        if (this.allPages.type === 'ready') {
            return this.allPages.value.sort((page1: PagesGetType, page2: PagesGetType) =>
                page1.title.localeCompare(page2.title)
            );
        }
        return [];
    }

    @action public setDefaultState = (): void => {
        this.setPage('add');
        this.editedPage = null;
        this.form.reset();
    };

    @action public onRemove = async (element: PagesGetType): Promise<void> => {
        const removeResult = element.id === undefined ? null : await apiCommon.deletePage.run({ id: element.id });

        if (removeResult === null) {
            // this.errorMessage = 'Unknown error. Something went wrong.';
        } else {
            await this.pagesResource.refresh();

            this.form.reset();
        }
    };

    @action public submitPage = async (page: PageType): Promise<number | Error | null> => {
        let response: number | Error | null;
        if (this.universe !== null) {
            try {
                const pageBody = {
                    page: {
                        slug: page.slug,
                        title: page.title,
                        content: page.content,
                        page_description: page.page_description,
                        keywords: page.keywords,
                        seo_title: page.seo_title,
                        page_language: this.language,
                        universe: this.universe,
                    },
                };
                response = await apiCommon.postPage.run(pageBody);
            } catch (error) {
                console.error(error);
                return null;
            }
            return response;
        }
        return null;
    };

    @action public submitEditedPage = async (page: PagesGetType, id: number): Promise<number | Error | null> => {
        let response: number | Error | null;
        if (this.universe !== null) {
            try {
                const pageObject = {
                    page: {
                        slug: page.slug,
                        title: page.title,
                        content: page.content,
                        page_description: page.page_description,
                        keywords: page.keywords,
                        seo_title: page.seo_title,
                        universe: this.universe,
                        page_language: this.language,
                    },
                    id: id,
                };
                response = await apiCommon.putPage.run(pageObject);
            } catch (error) {
                console.error(error);
                return null;
            }
            return response;
        }

        return null;
    };

    @action public handleSubmit = async (): Promise<void> => {
        this.isLoading = true;
        // this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok') {
            const payload: PageItem = {
                title: result.value.data.title,
                page_description: result.value.data.page_description,
                slug: result.value.data.slug,
                seo_title: result.value.data.seo_title,
                keywords: result.value.data.keywords,
                content: result.value.data.content,
                page_language: this.language,
                universe: this.universe,
            };

            const submitResult =
                this.editedPage !== null && this.editedPage.id !== undefined
                    ? await this.submitEditedPage(payload, this.editedPage.id)
                    : await this.submitPage(payload);
            if (submitResult === null || submitResult instanceof Error) {
                // this.errorMessage = 'Unknown error. Something went wrong.';
            } else {
                await this.pagesResource.refresh();
                this.setDefaultState();
            }
        }

        this.isLoading = false;
    };

    @action public setPage = (value: CurrentPageType): void => {
        this.page = value;
    };
}
